/**
 * Hook: Deposit flow — request deposit address, wait for confirmation, receive tokens.
 */

import { useState, useCallback, useRef } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { generateSecret, blindMessage, serializeBlindedMessage, constructUnblindedSignature, pointFromHex, type RawBlindedMessage } from '@/lib/cashu/blind-signature';
import { splitAmount } from '@/lib/cashu/split';
import { generateRequestId } from '@/lib/cashu/token';
import { createSeal, createGiftWrap, unwrapMessage } from '@/lib/nostr/gift-wrap';
import { getRequestKind, getResponseKind } from '@/lib/nostr/operations';
import type { RequestDepositRequest, DepositAddressResponse, DepositTokensResponse, DepositStatusResponse } from '@/lib/nostr/operations';
import { addProofs, addTransaction } from '@/lib/storage/wallet-store';
import type { Keyset } from '@/lib/cashu/keyset';

export type DepositState =
  | { step: 'idle' }
  | { step: 'preparing' }
  | { step: 'requesting' }
  | { step: 'awaiting_address' }
  | { step: 'address_ready'; depositAddress: string; paymentId?: string; requestId: string; amount: number }
  | { step: 'awaiting_deposit'; requestId: string }
  | { step: 'confirming'; requestId: string; confirmations: number; required: number; txHash: string }
  | { step: 'issuing_tokens'; requestId: string }
  | { step: 'done'; requestId: string; amount: number }
  | { step: 'error'; error: string };

export function useDepositFlow() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [state, setState] = useState<DepositState>({ step: 'idle' });
  const blindedDataRef = useRef<RawBlindedMessage[]>([]);
  const subRef = useRef<any>(null);

  /** Start a deposit: generate blinded messages and request address */
  const startDeposit = useCallback(async (
    amount: number,
    mintPubkey: string,
    keysets: Keyset[],
    userPrivkey: string,
  ) => {
    if (!user) {
      setState({ step: 'error', error: 'Not logged in' });
      return;
    }

    try {
      setState({ step: 'preparing' });

      const activeKeyset = keysets.find(k => k.active);
      if (!activeKeyset) {
        setState({ step: 'error', error: 'Mint has no active keyset' });
        return;
      }
      const maxDenom = Math.max(...Object.keys(activeKeyset.pubkeys).map(Number));
      const denominations = splitAmount(amount, maxDenom);
      for (const d of denominations) {
        if (!(d in activeKeyset.pubkeys)) {
          setState({ step: 'error', error: `Keyset missing denomination ${d}` });
          return;
        }
      }

      const rawMessages: RawBlindedMessage[] = denominations.map((denom) => {
        const raw = blindMessage(new TextEncoder().encode(generateSecret()));
        raw.amount = denom;
        return raw;
      });
      blindedDataRef.current = rawMessages;

      const requestId = generateRequestId();
      const request: RequestDepositRequest = {
        op: 'request_deposit',
        requestId,
        amount,
        blindedMessages: rawMessages.map(serializeBlindedMessage),
      };

      const rumor = {
        kind: getRequestKind(),
        content: JSON.stringify(request),
        tags: [['p', mintPubkey]],
        created_at: Math.floor(Date.now() / 1000),
        pubkey: user.pubkey,
      };

      const seal = createSeal(rumor, userPrivkey, mintPubkey);
      const giftWrap = createGiftWrap(seal, mintPubkey);

      setState({ step: 'requesting' });
      // Publish directly via nostr pool — gift wrap is signed by random key
      await nostr.event(giftWrap, { signal: AbortSignal.timeout(15000) });
      setState({ step: 'awaiting_address', ...({ requestId, amount } as any) });

      subRef.current = nostr.sub([{
        kinds: [1059],
        '#p': [user.pubkey],
        limit: 100,
      }]);

      subRef.current.on('event', async (event: any) => {
        try {
          const { rumor: responseRumor } = unwrapMessage(event, userPrivkey);
          if (responseRumor.kind !== getResponseKind()) return;

          const response = JSON.parse(responseRumor.content);
          if (response.requestId !== requestId) return;

          if (response.status === 'error') {
            subRef.current?.unsub();
            setState({ step: 'error', error: response.error });
            return;
          }

          if (response.status === 'ok' && response.depositAddress) {
            subRef.current?.unsub();
            const addr = response as DepositAddressResponse;
            setState({
              step: 'address_ready',
              depositAddress: addr.depositAddress,
              paymentId: addr.paymentId,
              requestId,
              amount,
            });
          } else if (response.status === 'pending') {
            const status = response as DepositStatusResponse;
            if (status.txHash) {
              setState({
                step: 'confirming',
                requestId,
                confirmations: status.confirmations,
                required: status.required,
                txHash: status.txHash,
              });
            } else {
              setState({ step: 'awaiting_deposit', requestId });
            }
          } else if (response.status === 'ok' && response.blindedSignatures) {
            subRef.current?.unsub();
            const tokens = response as DepositTokensResponse;
            setState({ step: 'issuing_tokens', requestId });

            const activeKeyset = keysets.find(k => k.active)!;
            const proofs: any[] = [];

            for (let i = 0; i < tokens.blindedSignatures.length; i++) {
              const bs = tokens.blindedSignatures[i];
              const raw = blindedDataRef.current[i];
              if (!raw || !raw.secret) continue;

              const pubkeyHex = activeKeyset.pubkeys[raw.amount];
              if (!pubkeyHex) continue;
              const pubKeyPoint = pointFromHex(pubkeyHex);

              const unblinded = constructUnblindedSignature(
                { C_: bs.C_, id: bs.id },
                raw.r,
                raw.secret,
                pubKeyPoint,
                raw.amount,
              );

              proofs.push({
                secret: unblinded.secret,
                C: unblinded.C,
                id: unblinded.id,
                amount: unblinded.amount,
                mintNpub: mintPubkey,
                acquiredAt: Date.now(),
              });
            }

            await addProofs(proofs);
            await addTransaction({
              id: requestId,
              type: 'deposit',
              mintNpub: mintPubkey,
              amount,
              timestamp: Date.now(),
              status: 'confirmed',
              txHash: tokens.txHash,
            });

            setState({ step: 'done', requestId, amount });
          }
        } catch {
          // Skip invalid messages
        }
      });
    } catch (err) {
      setState({ step: 'error', error: (err as Error).message });
    }
  }, [user, nostr]);

  const reset = useCallback(() => {
    subRef.current?.unsub();
    setState({ step: 'idle' });
    blindedDataRef.current = [];
  }, []);

  return { state, startDeposit, reset };
}
